<template>
  <v-container fluid class="pa-md-12">
    <v-row>
      <v-col cols="12">
        <v-card class="pa-5 px-md-12 py-md-6">
          <v-row v-if="funFullPageLoader">
            <v-col cols="12" class="text-center">
              <Vue3Lottie
                :animationData="FunLoaderJSON"
                :width="200"
                :height="200"
                v-motion
                :initial="{ opacity: 0, y: 30 }"
                :enter="{ opacity: 1, y: 0, scale: 1 }"
                :delay="300"
              />
              <div
                v-motion
                :initial="{ opacity: 0, y: 30 }"
                :enter="{ opacity: 1, y: 0, scale: 1 }"
                :delay="600"
                class="text-caption"
              >
                Generating...
              </div>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col cols="12">
              <div
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="0"
                class="font-weight-bold"
              >
                Create Session {{ allowFreeTrial ? "(Free Trial)" : "" }}
              </div>
            </v-col>

            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>

            <v-col cols="12" class="ma-0 pa-0 pl-3">
              <div class="text-h5 mt-6">Select Mode</div>
              <v-radio-group
                v-model="mode"
                class="mt-4"
                :disabled="allowFreeTrial || mustSubscribe"
              >
                <v-radio
                  label="Tutor Mode"
                  value="tutor"
                  color="primary"
                ></v-radio>
                <v-radio
                  label="Mock Mode"
                  value="mock"
                  color="primary"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <v-col v-if="mode === 'mock'" cols="12" class="ma-0 pa-0 pl-3">
              <div class="text-h5 mt-6">Select Type</div>
              <v-radio-group
                v-model="mockType"
                class="mt-4"
                :disabled="allowFreeTrial || mustSubscribe"
              >
                <v-radio label="OSCE" value="osce" color="primary"></v-radio>
                <v-radio
                  label="VIVA"
                  value="viva"
                  color="primary"
                  :disabled="distinctParents.length < 2"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <v-col cols="12">
              <v-alert
                class="mb-4"
                icon="mdi-school-outline"
                :title="mode === 'mock' ? 'Mock Mode' : 'Tutor Mode'"
                :text="mode === 'mock' ? mockAlertText(bank) : tutorAlertText"
                :type="mode === 'mock' ? 'success' : 'info'"
                variant="tonal"
              ></v-alert>
            </v-col>

            <!-- Tutor mode: selection UI -->
            <v-col v-if="mode === 'tutor'" cols="12">
              <div class="text-h5 mb-4">Select Categories</div>

              <v-checkbox
                :model-value="isAllSelected"
                label="Select All"
                @update:modelValue="handleMasterCheckbox"
                :indeterminate="showMasterIndeterminate"
                color="progress"
                :disabled="allowFreeTrial || mustSubscribe"
                class="checkbox-align"
              ></v-checkbox>

              <!-- Nested mode (multiple parent categories) -->
              <template v-if="useNested">
                <v-expansion-panels multiple>
                  <v-expansion-panel
                    v-for="pg in parentGroups"
                    :key="pg.parent"
                  >
                    <v-expansion-panel-title>
                      <v-row
                        no-gutters
                        class="align-center w-100 parent-title-row"
                      >
                        <v-col
                          cols="2"
                          md="1"
                          class="d-flex align-center checkbox-col"
                        >
                          <v-checkbox
                            :model-value="isParentFullySelected(pg.parent)"
                            @update:modelValue="
                              (val) => handleParentCheckbox(val, pg.parent)
                            "
                            @click.stop
                            @mousedown.stop
                            @keydown.stop
                            :indeterminate="
                              isParentPartiallySelected(pg.parent)
                            "
                            color="progress"
                            density="compact"
                            :disabled="allowFreeTrial || mustSubscribe"
                            hide-details
                            class="ma-0 pa-0 checkbox-align"
                          />
                        </v-col>

                        <v-col cols="10" md="11" class="align-self-center">
                          <div
                            class="d-flex align-center justify-space-between w-100"
                          >
                            <div class="pb-2">
                              {{
                                bank === "part-2-video-bank"
                                  ? (pg.parent || "").toUpperCase()
                                  : pg.parent
                              }}
                              <span class="text-caption text-disabled">
                                ({{ totalStationsInParent(pg.parent) }}
                                stations)
                              </span>
                            </div>

                            <!-- Parent header stats -->
                            <div v-if="!mobile" class="parent-header-stats">
                              <div class="stats-progress">
                                <v-progress-linear
                                  :model-value="
                                    parentHeaderStats[pg.parent]?.percentage ??
                                    0
                                  "
                                  color="progress"
                                  rounded
                                  height="8"
                                />
                              </div>
                              <div
                                class="stats-text text-caption text-disabled"
                              >
                                <span class="stat-item">
                                  <span class="stat-label">Attempted:</span>
                                  <span class="stat-value">
                                    {{
                                      parentHeaderStats[pg.parent]?.completed ??
                                      0
                                    }}
                                    /
                                    {{
                                      parentHeaderStats[pg.parent]?.total ?? 0
                                    }}
                                    ({{
                                      parentHeaderStats[pg.parent]
                                        ?.percentage ?? 0
                                    }}%)
                                  </span>
                                </span>
                                <span class="stat-divider">•</span>
                                <span class="stat-item">
                                  <span class="stat-label">Avg Score:</span>
                                  <span class="stat-value">
                                    {{
                                      parentHeaderStats[pg.parent]
                                        ?.averageScore ?? 0
                                    }}%
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text>
                      <v-table class="category-table">
                        <thead>
                          <tr>
                            <th
                              style="width: 50px"
                              class="text-left font-weight-bold"
                            ></th>
                            <th class="text-left font-weight-bold">Category</th>
                            <th class="text-left font-weight-bold">Stations</th>
                            <th
                              v-if="!mobile"
                              class="text-left font-weight-bold"
                            >
                              Stats
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="cat in pg.categories"
                            :key="`${pg.parent}::${cat.category}`"
                            class="align-middle"
                          >
                            <td class="checkbox-cell">
                              <v-checkbox
                                :model-value="
                                  isCategoryFullySelectedNested(
                                    pg.parent,
                                    cat.category,
                                  )
                                "
                                @update:modelValue="
                                  (val) =>
                                    handleCategoryCheckboxNested(
                                      val,
                                      pg.parent,
                                      cat.category,
                                    )
                                "
                                @click.stop
                                :indeterminate="
                                  isCategoryPartiallySelectedNested(
                                    pg.parent,
                                    cat.category,
                                  )
                                "
                                color="progress"
                                density="compact"
                                :disabled="allowFreeTrial || mustSubscribe"
                                hide-details
                                class="ma-0 pa-0 checkbox-align"
                              />
                            </td>

                            <td
                              :style="`${mobile ? 'font-size: smaller' : ''}`"
                            >
                              {{ cat.category }}
                            </td>

                            <td
                              :style="`${mobile ? 'font-size: smaller' : ''}`"
                            >
                              {{ cat.stations.length }} Station{{
                                cat.stations.length !== 1 ? "s" : ""
                              }}
                            </td>

                            <td v-if="!mobile" class="stats-cell pa-2">
                              <v-progress-linear
                                class="mb-1"
                                :model-value="
                                  getCategoryCompletionPercentage(cat.stations)
                                "
                                color="progress"
                                rounded
                                height="10"
                              />
                              <div class="cat-stats text-caption text-disabled">
                                <div class="label">Attempted</div>
                                <div class="value">
                                  {{
                                    getCategoryCompletionStats(cat.stations)
                                      .completed
                                  }}
                                  of
                                  {{
                                    getCategoryCompletionStats(cat.stations)
                                      .total
                                  }}
                                  ({{
                                    getCategoryCompletionPercentage(
                                      cat.stations,
                                    )
                                  }}%)
                                </div>

                                <div class="label">Average Score</div>
                                <div class="value">
                                  {{ getCategoryAverageScore(cat.stations) }}%
                                </div>

                                <div class="label">Flagged</div>
                                <div class="value">
                                  {{ getCategoryFlagged(cat.stations) }}
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>

              <!-- Single parent mode (flat list by category) -->
              <template v-else>
                <v-card
                  v-for="cat in categoriesFlat"
                  :key="cat.category"
                  class="mb-3 elevation-3"
                >
                  <v-card-text>
                    <v-row no-gutters class="align-center fill-height">
                      <v-col cols="2" md="1">
                        <div class="d-flex align-center h-100">
                          <v-checkbox
                            :model-value="isCategoryFullySelected(cat.stations)"
                            @update:modelValue="
                              (val) =>
                                handleCategoryCheckboxFlat(val, cat.category)
                            "
                            :indeterminate="
                              isCategoryPartiallySelected(cat.stations)
                            "
                            color="progress"
                            density="compact"
                            :disabled="allowFreeTrial || mustSubscribe"
                            hide-details
                            class="ma-0 pa-0 checkbox-align"
                          />
                        </div>
                      </v-col>

                      <v-col md="4" cols="6">
                        <span class="text-caption text-md-body-1">
                          {{ cat.category }}
                        </span>
                      </v-col>

                      <v-col
                        md="2"
                        cols="4"
                        class="text-caption text-medium-emphasis"
                      >
                        {{ cat.stations.length }} Stations
                      </v-col>

                      <v-col v-if="!mobile" cols="5" class="pr-6">
                        <v-progress-linear
                          class="mb-1"
                          :model-value="
                            getCategoryCompletionPercentage(cat.stations)
                          "
                          color="progress"
                          rounded
                          height="10"
                        />
                        <span class="text-caption text-disabled">
                          Attempted:
                          {{ getCategoryCompletionPercentage(cat.stations) }}%
                          ({{
                            getCategoryCompletionStats(cat.stations).completed
                          }}
                          of {{ cat.stations.length }})
                        </span>
                        <br />
                        <span class="text-caption text-disabled">
                          Average Score:
                          {{ getCategoryAverageScore(cat.stations) }}%
                        </span>
                        <br />
                        <span class="text-caption text-disabled">
                          Flagged:
                          {{ getCategoryFlagged(cat.stations) }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>
            </v-col>

            <!-- Options -->
            <v-col v-if="mode === 'tutor'" cols="12">
              <div class="text-h5 mb-6">Options</div>
              <v-radio-group v-model="options" class="mt-4">
                <v-radio
                  label="Unattempted stations only"
                  value="unattempted"
                  color="primary"
                ></v-radio>
                <v-radio
                  label="Attempted stations only"
                  value="attempted"
                  color="primary"
                ></v-radio>
                <v-radio
                  label="All stations"
                  value="all"
                  color="primary"
                ></v-radio>
                <v-radio
                  label="Flagged stations only"
                  value="flagged"
                  color="primary"
                ></v-radio>
              </v-radio-group>

              <v-text-field
                class="mt-6"
                v-model="numberOfStations"
                color="progress"
                base-color="progress"
                label="Number of Stations"
                type="number"
                :hint="`Max: ${possibleStations} station${
                  possibleStations !== 1 ? 's' : ''
                } (based on options).`"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12" class="mt-4">
              <v-divider></v-divider>
            </v-col>

            <v-col v-if="allowFreeTrial" cols="12">
              <v-alert
                icon="mdi-information"
                title="Free Trial"
                text="A random station will be selected for your free trial session."
                type="success"
                variant="tonal"
              ></v-alert>
            </v-col>

            <v-col cols="12">
              <v-btn
                v-if="!mustSubscribe"
                color="primary"
                rounded="lg"
                variant="flat"
                block
                size="large"
                :loading="btnLoading"
                :disabled="disableStartButton"
                @click="handleStartClick"
              >
                Start
              </v-btn>

              <v-btn
                v-else
                color="primary"
                rounded="lg"
                variant="flat"
                block
                size="large"
                :loading="btnLoading"
                @click="goToEyeAcademyWebsite"
                class="nice-grad-background"
              >
                Subscribe
              </v-btn>
              <div
                v-if="stationsSelected.length === 0"
                class="text-disabled mt-3"
              >
                You haven't selected any categories using the checkboxes above.
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import FunLoaderJSON from "@/assets/funLoader.json";
import { ref, computed, watch } from "vue";
import { useStatsStore } from "@/store/stats";
import { useDataStore } from "@/store/data";
import { useUserStore } from "@/store/user";
import { useAlertStore } from "@/store/alert";
import { useCollection, useFirestore } from "vuefire";
import { useDisplay } from "vuetify";
import { collection } from "firebase/firestore";
import router from "@/router";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useRoute } from "vue-router";
import { usePaymentStore } from "@/store/payment";

const { mobile } = useDisplay();
const db = useFirestore();
const functions = getFunctions();
functions.region = "europe-west2";

const statsStore = useStatsStore();
const dataStore = useDataStore();
const userStore = useUserStore();
const alertStore = useAlertStore();
const paymentStore = usePaymentStore();

const route = useRoute();

const bankCollection = route.path.includes("part-2-video-bank")
  ? "part-2-video-bank"
  : "interview-course";

const bank = route.path.includes("part-2-video-bank")
  ? "part-2-video-bank"
  : "interview-bank";

// Firestore: load stations
const stations = useCollection(
  collection(db, `new_modules/${bankCollection}/questions`),
);

// UI state
const funFullPageLoader = ref(false);
const btnLoading = ref(false);

// Mode and options
const mode = ref("tutor");
const mockType = ref("osce");
const options = ref("unattempted");
const numberOfStations = ref(0);

const tutorAlertText =
  "Customise your session by selecting categories, options, and the number of stations. Great for targeted practice.";

const mockAlertText = (bank) => {
  if (bank === "interview-bank") {
    // 🧠 Interview Bank (simpler rule)
    return "One station will be randomly selected from each category to simulate a real mock exam.";
  }

  if (bank === "part-2-video-bank") {
    if (mockType.value === "osce") {
      return "Three stations will be randomly selected from each category in the OSCE bank to simulate a real oral exam.";
    }

    if (mockType.value === "viva") {
      return "One station from each category in the VIVA bank will be selected, except 'Patient Management', which will have two stations to simulate a real exam.";
    }
  }

  // Just in case a new bank type appears
  return "Stations will be selected according to the chosen mock mode.";
};

// Selection
const stationsSelected = ref([]);

// Free trial / subscription
const allowFreeTrial = computed(() => {
  const notSubbed = !paymentStore.isSubscribed?.[bank]?.val;
  const numberOfSessions = dataStore.sessionsDataOrdered.length;
  return notSubbed && numberOfSessions === 0;
});

const mustSubscribe = computed(() => {
  return !paymentStore.isSubscribed?.[bank]?.val && !allowFreeTrial.value;
});

watch(
  () => allowFreeTrial.value,
  (val) => {
    if (val) {
      stationsSelected.value = [];
    }
  },
);

// Utility: video category order (for part-2-video-bank)
const videoOrderOSCE = [
  "Anterior Segment",
  "Glaucoma & Lids",
  "Posterior Segment",
  "Strabismus & Orbit",
  "Neuro-Ophthalmology",
];

const videoOrderVIVA = [
  "Patient Management",
  "Investigations",
  "Attitudes, Ethics & Responsibilities",
  "Health promotion, Audit, Research and EBM",
  "Communication Skills",
];

const categoryOrder = computed(() => {
  if (bank === "part-2-video-bank") {
    if (mode.value === "mock" && mockType.value === "viva") {
      return videoOrderVIVA;
    }
    return videoOrderOSCE;
  }
  return [];
});

// Distinct parents and grouping
const distinctParents = computed(() => {
  const set = new Set();
  (stations.value || []).forEach((s) => {
    set.add(s.parentCategory || "Uncategorised");
  });
  return Array.from(set);
});

const useNested = computed(() => distinctParents.value.length > 1);

// Parent -> categories -> stations
const parentGroups = computed(() => {
  if (!stations.value) return [];
  const map = {};

  stations.value.forEach((s) => {
    const parent = s.parentCategory || "Uncategorised";
    const category = s.category || "Uncategorised";

    if (!map[parent]) map[parent] = {};
    if (!map[parent][category]) map[parent][category] = [];
    map[parent][category].push(s);
  });

  const groups = Object.keys(map)
    .sort((a, b) => a.localeCompare(b))
    .map((parent) => {
      let categories = Object.keys(map[parent]).map((cat) => ({
        category: cat,
        stations: map[parent][cat],
      }));

      if (bank === "part-2-video-bank") {
        categories = categories.sort(
          (a, b) =>
            categoryOrder.value.indexOf(a.category) -
            categoryOrder.value.indexOf(b.category),
        );
      } else {
        categories = categories.sort((a, b) =>
          a.category.localeCompare(b.category),
        );
      }

      return {
        parent,
        categories,
      };
    });

  return groups;
});

// Flat categories for single parent mode
const categoriesFlat = computed(() => {
  if (!stations.value) return [];
  const map = {};

  stations.value.forEach((s) => {
    const category = s.category || "Uncategorised";
    if (!map[category]) map[category] = [];
    map[category].push(s);
  });

  let cats = Object.keys(map).map((c) => ({
    category: c,
    stations: map[c],
  }));

  if (bank === "part-2-video-bank") {
    cats = cats.sort(
      (a, b) =>
        categoryOrder.value.indexOf(a.category) -
        categoryOrder.value.indexOf(b.category),
    );
  } else {
    cats = cats.sort((a, b) => a.category.localeCompare(b.category));
  }

  return cats;
});

// Selection helpers
const allStationIds = computed(() => (stations.value || []).map((s) => s.id));

const isAllSelected = computed(() => {
  const total = allStationIds.value.length;
  return total > 0 && stationsSelected.value.length === total;
});

const stationIdsInParent = (parent) =>
  (stations.value || [])
    .filter((s) => (s.parentCategory || "Uncategorised") === parent)
    .map((s) => s.id);

const stationIdsInCategoryNested = (parent, category) =>
  (stations.value || [])
    .filter(
      (s) =>
        (s.parentCategory || "Uncategorised") === parent &&
        (s.category || "Uncategorised") === category,
    )
    .map((s) => s.id);

const stationIdsInCategoryFlat = (category) =>
  (stations.value || [])
    .filter((s) => (s.category || "Uncategorised") === category)
    .map((s) => s.id);

const handleMasterCheckbox = (checked) => {
  if (checked) {
    stationsSelected.value = [...allStationIds.value];
  } else {
    stationsSelected.value = [];
  }
};

const showMasterIndeterminate = computed(() => {
  const total = allStationIds.value.length;
  const selected = stationsSelected.value.length;
  return selected > 0 && selected < total;
});

const isParentFullySelected = (parent) => {
  const ids = stationIdsInParent(parent);
  return (
    ids.length > 0 && ids.every((id) => stationsSelected.value.includes(id))
  );
};

const isParentPartiallySelected = (parent) => {
  const ids = stationIdsInParent(parent);
  if (!ids.length) return false;
  const selected = ids.filter((id) => stationsSelected.value.includes(id));
  return selected.length > 0 && selected.length < ids.length;
};

const handleParentCheckbox = (checked, parent) => {
  const ids = stationIdsInParent(parent);
  if (checked) {
    stationsSelected.value = [...new Set([...stationsSelected.value, ...ids])];
  } else {
    stationsSelected.value = stationsSelected.value.filter(
      (id) => !ids.includes(id),
    );
  }
};

const isCategoryFullySelectedNested = (parent, category) => {
  const ids = stationIdsInCategoryNested(parent, category);
  return (
    ids.length > 0 && ids.every((id) => stationsSelected.value.includes(id))
  );
};

const isCategoryPartiallySelectedNested = (parent, category) => {
  const ids = stationIdsInCategoryNested(parent, category);
  if (!ids.length) return false;
  const selected = ids.filter((id) => stationsSelected.value.includes(id));
  return selected.length > 0 && selected.length < ids.length;
};

const handleCategoryCheckboxNested = (checked, parent, category) => {
  const ids = stationIdsInCategoryNested(parent, category);
  if (checked) {
    stationsSelected.value = [...new Set([...stationsSelected.value, ...ids])];
  } else {
    stationsSelected.value = stationsSelected.value.filter(
      (id) => !ids.includes(id),
    );
  }
};

// Single parent (flat) helpers
const isCategoryFullySelected = (stationsArr) => {
  return (
    stationsArr.length > 0 &&
    stationsArr.every((s) => stationsSelected.value.includes(s.id))
  );
};

const isCategoryPartiallySelected = (stationsArr) => {
  const selectedCount = stationsArr.filter((s) =>
    stationsSelected.value.includes(s.id),
  ).length;
  return selectedCount > 0 && selectedCount < stationsArr.length;
};

const handleCategoryCheckboxFlat = (checked, category) => {
  const ids = stationIdsInCategoryFlat(category);
  if (checked) {
    stationsSelected.value = [...new Set([...stationsSelected.value, ...ids])];
  } else {
    stationsSelected.value = stationsSelected.value.filter(
      (id) => !ids.includes(id),
    );
  }
};

const totalStationsInParent = (parent) => {
  return stationIdsInParent(parent).length || 0;
};

// Stats helpers
const getCategoryCompletionStats = (stationsArr) => {
  if (!stationsArr?.length) return { completed: 0, total: 0 };

  const categoryStats = dataStore.interviewSessionsStats?.categoryStats || {};
  const category = stationsArr[0]?.category || "Uncategorised";
  const stats = categoryStats[category] || {
    attempted: 0,
    total: stationsArr.length,
  };

  return {
    completed: stats.attempted ?? 0,
    total: stats.total ?? stationsArr.length,
  };
};

const getCategoryCompletionPercentage = (stationsArr) => {
  const stats = getCategoryCompletionStats(stationsArr);
  return stats.total > 0
    ? Math.round((stats.completed / stats.total) * 100)
    : 0;
};

const getCategoryAverageScore = (stationsArr) => {
  const stationStats = dataStore.interviewSessionsStats?.stationStats;
  if (!stationsArr?.length || !stationStats) return 0;

  const { averageScores } = stationStats;

  let totalScore = 0;
  let count = 0;

  stationsArr.forEach((station) => {
    if (averageScores?.[station.id] != null) {
      totalScore += averageScores[station.id];
      count++;
    }
  });

  return count > 0 ? Math.round(totalScore / count) : 0;
};

const getCategoryAverageScoreWithCount = (stationsArr) => {
  const stationStats = dataStore.interviewSessionsStats?.stationStats;
  if (!stationsArr?.length || !stationStats) return { avg: 0, count: 0 };

  const { averageScores } = stationStats;

  let totalScore = 0;
  let count = 0;

  stationsArr.forEach((station) => {
    if (averageScores?.[station.id] != null) {
      totalScore += averageScores[station.id];
      count++;
    }
  });

  return count > 0
    ? { avg: Math.round(totalScore / count), count }
    : { avg: 0, count: 0 };
};

const getCategoryFlagged = (stationsArr) => {
  const flaggedArray =
    bank === "interview-bank"
      ? userStore.basic?.flaggedInterviewBankStations || []
      : userStore.basic?.flaggedVideoBankStations || [];

  if (!stationsArr?.length) return 0;

  return stationsArr.reduce((count, station) => {
    return flaggedArray.includes(station.id) ? count + 1 : count;
  }, 0);
};

// Aggregate parent header stats
const parentHeaderStats = computed(() => {
  const res = {};
  (parentGroups.value || []).forEach((pg) => {
    let completed = 0;
    let total = 0;

    let avgSum = 0;
    let avgCount = 0;

    (pg.categories || []).forEach((cat) => {
      const comp = getCategoryCompletionStats(cat.stations);
      completed += comp.completed;
      total += comp.total;

      const { avg, count } = getCategoryAverageScoreWithCount(cat.stations);
      if (count > 0) {
        avgSum += avg;
        avgCount += 1;
      }
    });

    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    const averageScore = avgCount > 0 ? Math.round(avgSum / avgCount) : 0;

    res[pg.parent] = { completed, total, percentage, averageScore };
  });
  return res;
});

// Possible stations
const possibleStations = computed(() => {
  if (allowFreeTrial.value) {
    return 1;
  }

  const selectedStationsData =
    (stations.value || []).filter((s) =>
      stationsSelected.value.includes(s.id),
    ) || [];

  if (selectedStationsData.length === 0) return 0;

  const flaggedArray =
    bank === "interview-bank"
      ? userStore.basic?.flaggedInterviewBankStations || []
      : userStore.basic?.flaggedVideoBankStations || [];

  const attemptedStations = new Set(
    dataStore.interviewSessionsStats?.stationStats?.seenStationIds || [],
  );

  let result = 0;
  switch (options.value) {
    case "unattempted":
      result = selectedStationsData.filter(
        (s) => !attemptedStations.has(s.id),
      ).length;
      break;
    case "attempted":
      result = selectedStationsData.filter((s) =>
        attemptedStations.has(s.id),
      ).length;
      break;
    case "flagged":
      result = selectedStationsData.filter((s) =>
        flaggedArray.includes(s.id),
      ).length;
      break;
    case "all":
      result = selectedStationsData.length;
      break;
    default:
      result = 0;
  }

  return result;
});

watch(
  () => possibleStations.value,
  (val) => {
    numberOfStations.value = val;
  },
);

watch(
  () => numberOfStations.value,
  (val) => {
    if (val < 0) numberOfStations.value = 0;
    if (val > possibleStations.value) {
      numberOfStations.value = possibleStations.value;
    }
  },
);

// Disable start button
const disableStartButton = computed(() => {
  if (allowFreeTrial.value) return false;
  if (mode.value === "mock") return false;
  return (
    stationsSelected.value.length === 0 ||
    possibleStations.value === 0 ||
    numberOfStations.value === 0
  );
});

// Actions
const goToEyeAcademyWebsite = () => {
  window.open("https://eye.academy", "_blank");
};

const handleStartClick = async () => {
  if (dataStore.progressPending || dataStore.progressError) {
    alertStore.showAlert("Could not load attempted status. Please try again shortly.", "error");
    return;
  }
  btnLoading.value = true;

  try {
    if (allowFreeTrial.value) {
      const allowedStationIds =
        bank === "part-2-video-bank"
          ? [
              "9yMdBPGDnFFGMK0QNGyH",
              "HnzY6aEMVG9NfOBUjnTo",
              "EjmtGhQx6ZB1H0XMppJ7",
            ]
          : ["lBh3d81PkpaaUJMtUikp"];

      const availableStations =
        (stations.value || []).filter((station) =>
          allowedStationIds.includes(station.id),
        ) || [];

      if (availableStations.length === 0) {
        throw new Error("No stations available");
      }

      stationsSelected.value = availableStations.map((station) => station.id);
    } else if (mode.value === "mock") {
      const selectedStations = [];

      // 🧠 Case 1: Interview Bank (no parentCategory separation)
      if (bank === "interview-bank") {
        const categoriesMap = new Map();

        (stations.value || []).forEach((station) => {
          const cat = station.category || "Uncategorised";
          if (!categoriesMap.has(cat)) categoriesMap.set(cat, []);
          categoriesMap.get(cat).push(station);
        });

        // Interview-bank always 1 station per category
        const stationsPerCategory = 1;
        categoriesMap.forEach((categoryStations) => {
          const shuffled = [...categoryStations].sort(
            () => Math.random() - 0.5,
          );
          selectedStations.push(...shuffled.slice(0, stationsPerCategory));
        });
      }

      // 🧠 Case 2: Part 2 Oral Bank (has osce / viva)
      else if (bank === "part-2-video-bank") {
        if (mockType.value === "osce") {
          // --- OSCE Logic (keep current implementation)
          const categoriesMap = new Map();
          (stations.value || []).forEach((station) => {
            const cat = station.category || "Uncategorised";
            if (!categoriesMap.has(cat)) categoriesMap.set(cat, []);
            categoriesMap.get(cat).push(station);
          });

          // For part-2-video-bank, select 3 per category
          const stationsPerCategory = 3;
          categoriesMap.forEach((categoryStations) => {
            const shuffled = [...categoryStations].sort(
              () => Math.random() - 0.5,
            );
            selectedStations.push(...shuffled.slice(0, stationsPerCategory));
          });
        } else if (mockType.value === "viva") {
          // --- VIVA Logic (new)
          const vivaStations = (stations.value || []).filter(
            (s) => (s.parentCategory || "").toLowerCase() === "viva",
          );

          if (vivaStations.length === 0) {
            throw new Error("No stations available for VIVA mock type");
          }

          const categoriesMap = new Map();
          vivaStations.forEach((station) => {
            const cat = station.category || "Uncategorised";
            if (!categoriesMap.has(cat)) categoriesMap.set(cat, []);
            categoriesMap.get(cat).push(station);
          });

          categoriesMap.forEach((categoryStations, catName) => {
            const shuffled = [...categoryStations].sort(
              () => Math.random() - 0.5,
            );
            const numToSelect = catName === "Patient Management" ? 2 : 1;
            selectedStations.push(...shuffled.slice(0, numToSelect));
          });
        }
      }

      if (selectedStations.length === 0) {
        throw new Error("No stations available for the selected mock type");
      }

      stationsSelected.value = selectedStations.map((s) => s.id);
    } else {
      const attemptedStations = new Set(
        dataStore.interviewSessionsStats?.stationStats?.seenStationIds || [],
      );
      const averageScores =
        dataStore.interviewSessionsStats?.stationStats?.averageScores || {};
      const flaggedArray =
        bank === "interview-bank"
          ? userStore.basic?.flaggedInterviewBankStations || []
          : userStore.basic?.flaggedVideoBankStations || [];

      let eligibleStations =
        (stations.value || []).filter((station) =>
          stationsSelected.value.includes(station.id),
        ) || [];

      if (eligibleStations.length === 0) {
        throw new Error("No stations match the initial criteria");
      }

      switch (options.value) {
        case "attempted": {
          eligibleStations = eligibleStations
            .filter((s) => attemptedStations.has(s.id))
            .sort(
              (a, b) =>
                (averageScores[a.id] ?? Infinity) -
                (averageScores[b.id] ?? Infinity),
            )
            .slice(
              0,
              Math.min(numberOfStations.value, eligibleStations.length),
            );
          break;
        }
        case "unattempted": {
          const byCategory = eligibleStations
            .filter((s) => !attemptedStations.has(s.id))
            .reduce((acc, s) => {
              const cat = s.category || "Uncategorised";
              if (!acc[cat]) acc[cat] = [];
              acc[cat].push(s);
              return acc;
            }, {});

          const categories = Object.keys(byCategory);
          const selected = [];

          while (selected.length < numberOfStations.value) {
            let added = false;
            for (const cat of categories) {
              if (byCategory[cat].length > 0) {
                const idx = Math.floor(Math.random() * byCategory[cat].length);
                const pick = byCategory[cat].splice(idx, 1)[0];
                selected.push(pick);
                added = true;
                if (selected.length >= numberOfStations.value) break;
              }
            }
            if (!added) break;
          }
          eligibleStations = selected;
          break;
        }
        case "all": {
          const attempted = eligibleStations
            .filter((s) => attemptedStations.has(s.id))
            .sort(
              (a, b) =>
                (averageScores[a.id] ?? Infinity) -
                (averageScores[b.id] ?? Infinity),
            );
          const unattempted = eligibleStations.filter(
            (s) => !attemptedStations.has(s.id),
          );

          const alternating = [];
          const maxLen = Math.max(attempted.length, unattempted.length);
          for (let i = 0; i < maxLen; i++) {
            if (unattempted[i]) alternating.push(unattempted[i]);
            if (attempted[i]) alternating.push(attempted[i]);
          }

          eligibleStations = alternating.slice(
            0,
            Math.min(numberOfStations.value, alternating.length),
          );
          break;
        }
        case "flagged": {
          eligibleStations = eligibleStations
            .filter((s) => flaggedArray.includes(s.id))
            .sort(() => Math.random() - 0.5)
            .slice(
              0,
              Math.min(numberOfStations.value, eligibleStations.length),
            );
          break;
        }
      }

      if (eligibleStations.length === 0) {
        throw new Error("No stations match the selected criteria");
      }

      stationsSelected.value = eligibleStations.map((s) => s.id);
    }

    // Sort selected stations
    const selectedStationsData = (stations.value || []).filter((station) =>
      stationsSelected.value.includes(station.id),
    );

    let sortedStationIds;
    if (bank === "part-2-video-bank") {
      sortedStationIds = selectedStationsData
        .sort(
          (a, b) =>
            categoryOrder.value.indexOf(a.category || "Uncategorised") -
            categoryOrder.value.indexOf(b.category || "Uncategorised"),
        )
        .map((s) => s.id);
    } else {
      sortedStationIds = selectedStationsData
        .sort((a, b) =>
          (a.category || "Uncategorised").localeCompare(
            b.category || "Uncategorised",
          ),
        )
        .map((s) => s.id);
    }

    // Create session
    const createInterviewSession = httpsCallable(
      functions,
      "createInterviewSession",
    );
    const result = await createInterviewSession({
      bank,
      stationsSelected: sortedStationIds,
    });

    funFullPageLoader.value = true;

    if (result.data.success) {
      setTimeout(() => {
        router.push(`/sessions/${bank}/${result.data.sessionId}`);
      }, 2000);
    } else {
      throw new Error("Failed to create session");
    }
  } catch (error) {
    setTimeout(() => {
      funFullPageLoader.value = false;
      btnLoading.value = false;
      alertStore.showAlert(
        error.message ||
          "There was an error generating the session. Please try again or contact support.",
        "error",
      );
    }, 1000);
    return;
  } finally {
    btnLoading.value = false;
  }
};
</script>

<style scoped>
.nice-grad-background {
  background: linear-gradient(
    to right,
    rgb(236, 72, 153),
    rgb(239, 68, 68),
    rgb(234, 179, 8)
  ) !important;
}

.category-table th,
.category-table td {
  vertical-align: middle !important;
}

.checkbox-align {
  display: flex;
  align-items: center;
}

.checkbox-col {
  min-height: 36px;
}

.checkbox-cell {
  width: 50px;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.stats-cell {
  padding-left: 16px !important;
}

.parent-header-stats {
  white-space: nowrap;
  margin-left: 8px;
}

.cat-stats {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 8px;
  row-gap: 4px;
  align-items: baseline;
  margin-top: 6px;
}

.cat-stats .label::after {
  content: ":";
  margin-left: 2px;
}

@media (min-width: 600px) {
  .cat-stats {
    grid-template-columns: max-content 1fr max-content 1fr;
  }
}

.parent-title-row {
  min-height: 44px;
}

.parent-header-stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 16px;
  min-width: 280px;
}

.stats-progress {
  width: 100%;
}

.stats-text {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.stat-item {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.stat-label {
  font-weight: 500;
}

.stat-value {
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.stat-divider {
  opacity: 0.6;
  margin: 0 4px;
}

@media (min-width: 960px) {
  .parent-header-stats {
    min-width: 320px;
  }
}
</style>
